import React, { useEffect, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import { useApp } from "@/context/AppContext";
import { api } from "@/lib/api";
import { hasRole } from "@/lib/roles";
import { ensureAdSenseLoaded } from "@/lib/googleAds";
import { feedGoogleEnabled } from "@/lib/admob";
import { AdmobNativeCard } from "@/components/AdmobNative";

// Responsive Google AdSense unit, SPA-safe (one push per fresh <ins>).
// WEB ONLY: AdSense is forbidden inside native apps (AdMob covers native).
// Publisher/slots come from the admin config — nothing hardcoded.
export const GoogleAd = ({ slotKey = "feed_slot", routeKey = "", className = "", style }) => {
  const { config, t, user } = useApp();
  const isSuperAdmin = hasRole(user, "super_admin");
  const insRef = useRef(null);
  const pushed = useRef(false);
  const [adStatus, setAdStatus] = useState(null); // null (en attente) | "filled" | "unfilled"
  const adsense = config?.settings?.adsense || {};
  const publisher = (adsense.publisher_id || "").trim();
  const slot = String(adsense[slotKey] || "").trim();
  const enabled = !Capacitor.isNativePlatform() && adsense.enabled === true && !!publisher && !!slot;

  useEffect(() => {
    if (!enabled) return;
    ensureAdSenseLoaded(publisher);
    const ins = insRef.current;
    if (!ins || pushed.current) return;
    if (ins.getAttribute("data-adsbygoogle-status") || ins.getAttribute("data-ad-status")) {
      pushed.current = true;
      return;
    }
    pushed.current = true;
    window.adsbygoogle = window.adsbygoogle || [];
    try { window.adsbygoogle.push({}); }
    catch (e) { pushed.current = false; console.error("AdSense error:", e); }
  }, [enabled, slot, routeKey, publisher]);

  // La carte n'apparaît QUE lorsque Google confirme une annonce (data-ad-status="filled") : en
  // attente (réseau lent, bloqueur, compte en cours d'examen) ou « unfilled », le fil ne montre
  // jamais un grand cadre blanc « Publicité ». Le bloc garde sa LARGEUR (jamais display:none avant
  // la réponse, sinon Google répond toujours « unfilled »). Une fois rempli → une impression comptée.
  const counted = useRef(false);
  useEffect(() => {
    if (!enabled) return;
    const ins = insRef.current;
    if (!ins) return;
    const check = () => {
      const status = ins.getAttribute("data-ad-status");
      setAdStatus(status === "filled" || status === "unfilled" ? status : null);
      if (status === "filled" && !counted.current) {
        counted.current = true;
        const placement = slotKey === "sidebar_slot" ? "sidebar" : slotKey === "profile_slot" ? "profile" : "feed";
        api.post("/google-ads/impression", { platform: "web", placement }).catch(() => {});
      }
    };
    check();
    const mo = new MutationObserver(check);
    mo.observe(ins, { attributes: true, attributeFilter: ["data-ad-status"] });
    // Google ne pose aucun statut quand il REFUSE la requête (HTTP 400 : bloc inconnu/archivé) ou
    // quand un bloqueur coupe la demande → après 8 s sans réponse, on considère le bloc mort.
    const stall = setTimeout(() => { if (!ins.getAttribute("data-ad-status")) setAdStatus("stalled"); }, 8000);
    return () => { mo.disconnect(); clearTimeout(stall); };
  }, [enabled, slot, routeKey, slotKey]);

  if (!enabled) {
    // App native (ou QA préversion) : annonce NATIVE AdMob à la place d'AdSense (Fil et Profil ; jamais
    // la barre latérale, propre au web). Réglages et Ad Unit IDs : Admin › Paramètres › Finance (cartes Google AdMob).
    if (slotKey !== "sidebar_slot" && feedGoogleEnabled(config?.settings)) {
      return <AdmobNativeCard placement={slotKey === "profile_slot" ? "profile" : "feed"} routeKey={routeKey} />;
    }
    return null;
  }

  // Optional in-feed (fluid) rendering for the feed slot when the admin provides
  // the AdSense layout key — blends better between posts while staying labeled.
  const layoutKey = slotKey === "feed_slot" ? (adsense.feed_layout_key || "").trim() : "";
  const formatAttrs = layoutKey
    ? { "data-ad-format": "fluid", "data-ad-layout-key": layoutKey }
    : { "data-ad-format": "auto", "data-full-width-responsive": "true" };
  const testAttrs = adsense.test_mode !== false ? { "data-adtest": "on" } : {};

  const hiddenStyle = adStatus === "unfilled" || adStatus === "stalled"
    ? { display: "none" }
    : adStatus !== "filled" ? { height: 0, overflow: "hidden", opacity: 0, marginTop: 0, marginBottom: 0, padding: 0, border: 0, boxShadow: "none" } : undefined;

  // Super admin uniquement : quand Google ne sert AUCUNE annonce, un repère discret remplace le vide
  // pour distinguer « bloc absent » de « bloc présent mais non rempli / refusé par Google ».
  const adminHint = isSuperAdmin && (adStatus === "unfilled" || adStatus === "stalled") && (
    <div className={`rounded-xl border border-dashed px-3 py-2 text-[11px] leading-snug ${className}`} style={{ borderColor: "#D97706", color: "#92400E", background: "#FFFBEB" }} data-testid="google-ad-admin-hint" data-ad-state={adStatus}>
      <b>AdSense · bloc {slot}{adsense.test_mode !== false ? " · mode test" : ""}</b> — {adStatus === "unfilled"
        ? <>la demande est bien partie, Google n'a renvoyé <b>aucune annonce</b> (« unfilled ») : compte ou site pas encore approuvé (AdSense › Sites : yalola.app doit être « Prêt »), ou Centre des règles.</>
        : <>Google <b>n'a pas répondu</b> à la demande (requête refusée — ID de bloc inexistant/archivé dans ce compte — ou bloqueur de publicités). Vérifie l'ID dans AdSense › Annonces › Par bloc d'annonces.</>}
      {" "}Repère visible par les super admins seulement.
    </div>
  );

  return (<>
    {adminHint}
    <div
      className={`yalola-card overflow-hidden ${className}`}
      style={hiddenStyle}
      data-testid="google-ad"
      data-ad-state={adStatus || "pending"}
    >
      <div className="px-3 pt-2 text-[10px] font-bold uppercase tracking-wide" style={{ color: "var(--yalola-text-muted)" }} data-testid="google-ad-label">{t("ads.label", "Publicité")}</div>
      <ins
        key={`${slot}:${routeKey}`}
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block", minHeight: 90, ...style }}
        data-ad-client={publisher}
        data-ad-slot={String(slot)}
        {...formatAttrs}
        {...testAttrs}
      />
    </div>
  </>);
};

export default GoogleAd;
